import React from 'react'
import { I18n } from 'react-redux-i18n'
import { connect } from 'react-redux'
import {
  selectCheckInType,
  selectReservation,
  updateServiceInfo,
  loadServiceTxn,
} from '../../actions/checkin'
import { info } from '../../storage'

const connectCheckInType = connect(
  ({ checkin: { isPristine, isCreate }, fetch: { defaults } }) => ({
    isPristine,
    isCreate,
    defaultDurations:
      defaults && defaults.list ? defaults.list.durations : undefined,
  }),
  {
    selectCheckInType,
    selectReservation,
    updateServiceInfo,
    loadServiceTxn,
  },
)

export const withCheckInType = (Component) =>
  connectCheckInType(withCheckInTypeContainer(Component))

export const withCheckInTypeContainer = (Component) =>
  class CheckInTypeContainer extends React.Component {
    state = {
      showSaveDialog: false,
    }

    handleSelectCheckInType = (checkInType) => {
      const { isPristine, isCreate } = this.props

      if (!isPristine && !isCreate)
        this.setState({
          showSaveDialog: true,
          handleAfterSave: () => this.selectCheckInType(checkInType),
        })
      else this.selectCheckInType(checkInType)
    }

    selectCheckInType = (checkInType) => {
      const { serviceName, serviceClass } = info()

      this.props.selectCheckInType(checkInType)
      this.props.updateServiceInfo(
        serviceName,
        serviceClass,
        this.props.defaultDurations,
      )
    }

    handleSelectReservation = (reservation, serviceId) => {
      if (!this.props.isPristine)
        this.setState({
          showSaveDialog: true,
          handleAfterSave: () => this.selectReservation(reservation, serviceId),
        })
      else this.selectReservation(reservation, serviceId)
    }

    selectReservation = (reservation, serviceId) => {
      this.props.selectReservation(
        reservation,
        serviceId,
        this.updateServiceInfoIfNeeded,
      )
    }

    handleLoadServiceTxn = (serviceTxn) => {
      if (!this.props.isPristine)
        this.setState({
          showSaveDialog: true,
          handleAfterSave: () => this.loadServiceTxn(serviceTxn),
        })
      else this.loadServiceTxn(serviceTxn)
    }

    loadServiceTxn = (serviceTxn) => {
      this.props.loadServiceTxn(serviceTxn)
      this.updateServiceInfoIfNeeded(serviceTxn)
    }

    updateServiceInfoIfNeeded = (serviceTxn) => {
      const { serviceName, serviceClass } = info()
      if (
        serviceTxn &&
        serviceTxn.serviceInfo &&
        serviceTxn.serviceInfo.duration
      )
        return
      this.props.updateServiceInfo(
        serviceName,
        serviceClass,
        this.props.defaultDurations,
      )
    }

    handleSave = () => {
      if (this.props.onSubmit && this.state.handleAfterSave) {
        this.props.onSubmit(this.state.handleAfterSave)
        this.setState({ showSaveDialog: false })
      }
    }

    handleDontSave = () => {
      if (this.state.handleAfterSave) {
        this.state.handleAfterSave()
        this.setState({ showSaveDialog: false })
      }
    }

    handleHideSaveDialog = () => this.setState({ showSaveDialog: false })

    render() {
      return (
        <div style={{ height: 'inherit' }}>
          <Component
            {...this.props}
            isPristine={this.props.isPristine}
            selectCheckInType={this.handleSelectCheckInType}
            selectReservation={this.handleSelectReservation}
            loadServiceTxn={this.handleLoadServiceTxn}
          />

          <SaveDialog
            isShow={this.state.showSaveDialog}
            onHide={this.handleHideSaveDialog}
            onSave={this.handleSave}
            onDontSave={this.handleDontSave}
          />
        </div>
      )
    }
  }

const SaveDialog = ({ isShow, onHide, onDontSave, onSave }) => {
  if (!isShow) return null

  return (
    <div>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onHide}
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="relative bg-white rounded-lg shadow-xl max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {I18n.t('checkInScene.saveDialog.title')}
                </h3>
              </div>
            </div>
            <button
              onClick={onHide}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="p-6">
            <p className="text-gray-600 text-base">
              {I18n.t('checkInScene.saveDialog.description')}
            </p>
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-lg">
            <button
              onClick={onHide}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              {I18n.t('action.cancel')}
            </button>
            <button
              onClick={onDontSave}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              {I18n.t('action.dontSave')}
            </button>
            <button
              onClick={onSave}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              {I18n.t('action.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
